import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Plus, FileText, Edit2, Trash2, Copy, Download, Eye, Send, BookOpen } from 'lucide-react';
import EmailModal from '@/pages/EmailModal';

const TEMPLATE_TYPES = ['Quote', 'Follow-Up', 'Completion', 'Decline', 'Invoice', 'General'];
const PLACEHOLDERS = ['{{client_full_name}}', '{{client_first_name}}', '{{client_last_name}}', '{{client_email}}', '{{client_phone}}', '{{client_address}}', '{{property_address}}', '{{quote_type}}', '{{appraisal_status}}', '{{contract_amount}}', '{{appraisal_date}}', '{{date}}', '{{company_name}}'];

export default function LetterTemplatesPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({});
  const [previewData, setPreviewData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [clients, setClients] = useState([]);
  const [appraisals, setAppraisals] = useState([]);
  const [genClientId, setGenClientId] = useState('');
  const [genAppraisalId, setGenAppraisalId] = useState('');
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailData, setEmailData] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, cRes, aRes] = await Promise.all([
        api.get('/letter-templates'),
        api.get('/clients?limit=100'),
        api.get('/appraisals?limit=100'),
      ]);
      setTemplates(tRes.data || []);
      setClients(cRes.data.items || []);
      setAppraisals(aRes.data.items || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/letter-templates/${editId}`, form);
      else await api.post('/letter-templates', form);
      setShowForm(false); setEditId(null); setForm({}); load();
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    await api.delete(`/letter-templates/${id}`);
    load();
  };

  const generatePreview = async (templateId) => {
    try {
      const { data } = await api.post(`/letter-templates/${templateId}/generate`, { client_id: genClientId, appraisal_id: genAppraisalId });
      setPreviewData(data);
      setShowPreview(true);
    } catch (e) { console.error(e); }
  };

  const downloadPdf = async (templateId) => {
    try {
      const response = await api.post(`/letter-templates/${templateId}/generate-pdf`, { client_id: genClientId, appraisal_id: genAppraisalId }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `RTR_Letter_${templateId.slice(-6)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) { console.error(e); }
  };

  const insertPlaceholder = (placeholder) => {
    setForm(prev => ({ ...prev, body: (prev.body || '') + placeholder }));
  };

  return (
    <div data-testid="letter-templates-page" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope]">Letter Templates</h1>
          <p className="text-sm text-slate-500 mt-1">{templates.length} templates</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="open-reports-library"
            onClick={() => navigate('/reports-library')}
            className="flex items-center gap-2 px-4 py-2 border border-[#E2E2DF] text-slate-700 hover:border-[#D35400] hover:text-[#D35400] rounded-md text-sm font-semibold transition-colors"
            title="Browse the central Reports & Letters library"
          >
            <BookOpen size={16} /> Reports Library
          </button>
          <button data-testid="add-template-button" onClick={() => { setForm({ active: true }); setEditId(null); setShowForm(true); }} className="flex items-center gap-2 px-4 py-2 bg-[#D35400] hover:bg-[#A04000] text-white rounded-md text-sm font-semibold">
            <Plus size={16} /> New Template
          </button>
        </div>
      </div>
      {/* Context Selector */}
      <div className="bg-white border border-[#E2E2DF] rounded-md p-4">
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-2">Select context for generating letters</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-slate-500">Client</label>
            <select data-testid="letter-client-select" value={genClientId} onChange={e => setGenClientId(e.target.value)} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm">
              <option value="">Select Client</option>
              {clients.map(c => <option key={c._id} value={c._id}>{c.name_first} {c.name_last}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500">Appraisal</label>
            <select data-testid="letter-appraisal-select" value={genAppraisalId} onChange={e => setGenAppraisalId(e.target.value)} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm">
              <option value="">Select Appraisal</option>
              {appraisals.map(a => <option key={a._id} value={a._id}>{a.client_name || 'Unknown'} - {a.quote_type_ref || 'N/A'}</option>)}
            </select>
          </div>
        </div>
      </div>
      {loading ? <div className="text-center py-12 text-slate-400">Loading...</div> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {templates.map(t => (
            <div key={t._id} data-testid={`template-card-${t._id}`} className="bg-white border border-[#E2E2DF] rounded-md p-5 hover:shadow-sm transition-all">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <FileText size={18} className="text-[#4A6FA5]" />
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{t.title}</p>
                    {t.template_type && <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">{t.template_type}</span>}
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => generatePreview(t._id)} className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-[#4A6FA5]" title="Preview"><Eye size={13} /></button>
                  <button onClick={() => downloadPdf(t._id)} className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-[#2F855A]" title="Download PDF"><Download size={13} /></button>
                  <button onClick={async () => { const { data: gen } = await api.post(`/letter-templates/${t._id}/generate`, { client_id: genClientId, appraisal_id: genAppraisalId }); const selClient = clients.find(c => c._id === genClientId); setEmailData({ to: selClient?.email || '', subject: gen.subject, body: gen.body }); setEmailOpen(true); }} className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-purple-600" title="Send via Email"><Send size={13} /></button>
                  <button onClick={() => { setForm(t); setEditId(t._id); setShowForm(true); }} className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600"><Edit2 size={13} /></button>
                  <button onClick={() => handleDelete(t._id)} className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-500"><Trash2 size={13} /></button>
                </div>
              </div>
              {t.subject && <p className="text-xs text-slate-600 mb-2"><strong>Subject:</strong> {t.subject}</p>}
              <p className="text-xs text-slate-500 whitespace-pre-line line-clamp-4">{t.body}</p>
            </div>
          ))}
          {templates.length === 0 && <p className="col-span-full text-center py-12 text-slate-400">No templates yet</p>}
        </div>
      )}
      {/* Edit/Create Form */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-[#E2E2DF]"><h2 className="text-xl font-bold text-slate-900 font-[Manrope]">{editId ? 'Edit' : 'New'} Template</h2></div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">Title *</label><input data-testid="template-title-input" required value={form.title || ''} onChange={e => setForm({...form, title: e.target.value})} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm" /></div>
                <div><label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">Type</label><select value={form.template_type || ''} onChange={e => setForm({...form, template_type: e.target.value})} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm"><option value="">Select</option>{TEMPLATE_TYPES.map(t => <option key={t}>{t}</option>)}</select></div>
              </div>
              <div><label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">Subject Line</label><input value={form.subject || ''} onChange={e => setForm({...form, subject: e.target.value})} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm" placeholder="Re: {{quote_type}} - {{property_address}}" /></div>
              <div>
                <div className="flex items-center justify-between mb-1.5"><label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Body</label><span className="text-[10px] text-slate-400">Click to insert placeholders</span></div>
                <div className="flex flex-wrap gap-1 mb-2">
                  {PLACEHOLDERS.map(p => <button key={p} type="button" onClick={() => insertPlaceholder(p)} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 hover:bg-[#D35400]/10 hover:text-[#D35400] transition-colors">{p}</button>)}
                </div>
                <textarea data-testid="template-body-input" value={form.body || ''} onChange={e => setForm({...form, body: e.target.value})} rows={12} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm font-mono" placeholder="Dear {{client_full_name}},&#10;&#10;..." />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-[#E2E2DF] rounded-md text-sm hover:bg-slate-50">Cancel</button>
                <button data-testid="save-template-button" type="submit" className="px-4 py-2 bg-[#D35400] hover:bg-[#A04000] text-white rounded-md text-sm font-semibold">Save Template</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Preview Modal */}
      {showPreview && previewData && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowPreview(false)}>
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-[#E2E2DF]">
              <h2 className="text-xl font-bold text-slate-900 font-[Manrope]">Letter Preview</h2>
              <p className="text-xs text-slate-500 mt-1">{previewData.template_title}</p>
            </div>
            <div className="p-6">
              {previewData.subject && <p className="text-sm font-semibold text-slate-800 mb-4 pb-3 border-b border-[#E2E2DF]">Subject: {previewData.subject}</p>}
              <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-line">{previewData.body}</div>
            </div>
            <div className="p-4 border-t border-[#E2E2DF] flex justify-end gap-2">
              <button onClick={() => { navigator.clipboard.writeText(previewData.body); }} className="flex items-center gap-1 px-3 py-1.5 border border-[#E2E2DF] rounded-md text-sm hover:bg-slate-50"><Copy size={13} /> Copy</button>
              <button onClick={() => setShowPreview(false)} className="px-3 py-1.5 bg-[#D35400] text-white rounded-md text-sm font-semibold">Close</button>
            </div>
          </div>
        </div>
      )}
      <EmailModal isOpen={emailOpen} onClose={() => setEmailOpen(false)} defaultTo={emailData.to || ''} defaultSubject={emailData.subject || ''} defaultBody={emailData.body || ''} appraisalId={genAppraisalId} />
    </div>
  );
}
